import React, { useRef, useState, useEffect, useMemo } from 'react';
import JoditEditor from 'jodit-react';
import { MdCheck, MdCheckCircle, MdClose, MdEdit, MdErrorOutline, MdFactCheck, MdRefresh, MdReportGmailerrorred, MdDocumentScanner } from 'react-icons/md';
import { useAuth } from '../../../Context/AuthContext';
import { getAiSourceType } from '../AegisAiUi';
import AiHallucinationPanel from './AiHallucinationPanel';
import { clearPassageHighlight, highlightPassage } from './highlightPassage';
import useHallucinationReview from './useHallucinationReview';
import '../../../Styles/TextEditor.css';

// Word-by-word reveal: at least a few characters per frame, faster when a large backlog is waiting,
// so short text types out visibly and long documents still finish showing within about a second.
const MIN_CHARS_PER_FRAME = 5;
const BACKLOG_FRAMES = 18;

function nextWordBoundary(text, index) {
    if (index >= text.length) return text.length;
    const match = /\s/.exec(text.slice(index, index + 40));
    return match ? index + match.index : index;
}


function formatContentToHtml(raw) {
    if (!raw || typeof raw !== 'string') return '';
    const hasHtmlTags = /<[a-z][\s\S]*>/i.test(raw);
    if (!hasHtmlTags) {
        return raw
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .split(/\n{2,}/)
            .map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
            .join('');
    }
    const blockRegex = /(<p[\s\S]*?<\/p>|<div[\s\S]*?<\/div>|<table[\s\S]*?<\/table>|<h[1-6][\s\S]*?<\/h[1-6]>|<img[\s\S]*?>|<ul[\s\S]*?<\/ul>|<ol[\s\S]*?<\/ol>|<blockquote[\s\S]*?<\/blockquote>)/gi;
    const parts = raw.split(blockRegex);
    const formatted = parts
        .map((part) => {
            if (!part) return '';
            if (part.trim().startsWith('<') && part.trim().endsWith('>')) {
                return part.trim();
            }
            const trimmed = part.trim();
            if (!trimmed) return '';
            const escaped = trimmed
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
            return escaped
                .split(/\n{2,}/)
                .map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
                .join('');
        })
        .filter(Boolean)
        .join('');
    return normalizeImageMarkup(formatted);
}

function normalizeImageMarkup(html) {
    if (!html || typeof html !== 'string') return html;
    return html.replace(/<img\b([^>]*)>/gi, (match, attrs) => {
        let updatedAttrs = attrs.replace(/style=(["'])(.*?)\1/gi, (styleAttr, quote, styleStr) => {
            let cleaned = styleStr
                .replace(/\bmax-width\s*:\s*(min\([^)]+\)|360px|380px)\s*;?/gi, '')
                .replace(/\bmax-height\s*:\s*3[02]0px\s*;?/gi, '')
                .replace(/\bwidth\s*:\s*auto\s*;?/gi, '')
                .replace(/\bheight\s*:\s*auto\s*;?/gi, '')
                .trim();
            if (!/max-width/i.test(cleaned)) {
                cleaned = 'max-width:100%; ' + cleaned;
            }
            return `style=${quote}${cleaned}${quote}`;
        });
        return `<img${updatedAttrs}>`;
    });
}

const EXTRACTION_MODAL_JODIT_CONFIG = {
    readonly: false,
    placeholder: "Edit extracted content...",
    hidePoweredByJodit: true,
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultActionOnPaste: 'insert_as_html',
    defaultActionOnPasteFromWord: 'insert_as_html',
    processPasteHTML: true,
    showXPathInStatusbar: false,
    showCharsCounter: true,
    showWordsCounter: true,
    toolbarAdaptive: false,
    height: '100%',
    minHeight: 280,
    removeButtons: [
        "link", "hr", "symbol", "math", "video", "file",
        "speechRecognize", "print", "about", "fullsize", "preview", "classSpan",
        "superscript", "subscript", "lineHeight", "font", "copyformat"
    ],
    disablePlugins: [
        "about", "powered-by-jodit", "poweredByJodit", "symbol", "math", "video", "file", "link"
    ],
    buttons: [
        "bold", "italic", "underline", "strikethrough", "eraser", "|",
        "ul", "ol", "|",
        "paragraph", "fontsize", "|",
        "image", "table", "|",
        "brush", "|",
        "left", "center", "right", "justify", "|",
        "undo", "redo", "|",
        "cut", "copy", "paste", "selectall", "|",
        "find", "source"
    ],
    resizer: {
        showSize: true,
        hideSizeTimeout: 1000,
        forImageChangeAttributes: false,
        min_width: 20,
        min_height: 20,
    },
    image: {
        dialog: {
            openOnDblClick: true,
        },
        openOnDblClick: true,
        editSize: true,
        editAlt: true,
        editSrc: true,
    },
    uploader: {
        insertImageAsBase64URI: true,
    },
};

export default function AiExtractionPreviewModal({ source, extraction, onCancel, onConfirm, onRetry, saving = false, savingLabel, title = 'Review content', isEdit = false, isOcr = false }) {
    const ocrActive = typeof isOcr === 'boolean' ? isOcr : Boolean(source?.isOcr);
    const { token } = useAuth();
    // Hallucination risks are analyzed and displayed ONLY when uploading a new document (create mode).
    // On the edit page (isEdit is true, title is 'Edit document', or URL pathname includes /edit), it is strictly disabled.
    const isEditPage = Boolean(
        isEdit ||
        title === 'Edit document' ||
        (typeof window !== 'undefined' && window.location?.pathname?.includes('/edit'))
    );
    const enableHallucination = !isEditPage;
    const type = getAiSourceType(source.sourceType);
    const isCustomText = source.sourceType === 'custom_text';
    // Documents are shown on an A4-like page styled like the PDF they are saved as.
    const isDocument = source.sourceType === 'document';
    const { status, text, rawText, cleaning, pages, result, error } = extraction;

    const [isEditing, setIsEditing] = useState(false);
    const [hasUserEdited, setHasUserEdited] = useState(false);
    const [editedText, setEditedText] = useState('');
    const [editorInitialValue, setEditorInitialValue] = useState('');
    // Cleaned transcripts can be swapped back to the original; confirm saves whichever version is shown.
    const [showOriginal, setShowOriginal] = useState(false);
    // Whole-site pages the user unticked; they are left out of the preview and of what gets saved.
    const [excluded, setExcluded] = useState(() => new Set());
    useEffect(() => setExcluded(new Set()), [result]);
    const [shown, setShown] = useState(0);
    // The text last shown outside the editor; it is checked for hallucination risks, and stays checked while editing.
    const [checkedText, setCheckedText] = useState('');
    const [confirmingSubmit, setConfirmingSubmit] = useState(false);
    const review = useHallucinationReview(token, enableHallucination ? checkedText : '');

    const shownRef = useRef(0);
    const textRef = useRef(text);
    const bodyRef = useRef(null);
    const editorRef = useRef(null);
    const stickToBottomRef = useRef(true);
    textRef.current = text;

    // Body scroll lock: prevent background page scrolling while modal is open
    useEffect(() => {
        const originalBodyOverflow = document.body.style.overflow;
        const originalHtmlOverflow = document.documentElement.style.overflow;
        const mainEl = document.querySelector('.hc-main-content');
        const originalMainOverflow = mainEl ? mainEl.style.overflow : '';

        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        if (mainEl) mainEl.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = originalBodyOverflow;
            document.documentElement.style.overflow = originalHtmlOverflow;
            if (mainEl) mainEl.style.overflow = originalMainOverflow;
        };
    }, []);

    // Sync streaming extraction text to editedText if user hasn't started manual edits yet
    useEffect(() => {
        if (!hasUserEdited) {
            setEditedText(text || source.text || '');
        }
    }, [text, source.text, hasUserEdited]);

    // Word-by-word streaming reveal animation
    useEffect(() => {
        if (text.length < shownRef.current) {
            shownRef.current = 0;
            setShown(0);
        }
        let frame = 0;
        const step = () => {
            const full = textRef.current;
            const backlog = full.length - shownRef.current;
            if (backlog <= 0) return;
            const target = shownRef.current + Math.max(MIN_CHARS_PER_FRAME, Math.ceil(backlog / BACKLOG_FRAMES));
            shownRef.current = Math.min(full.length, nextWordBoundary(full, target));
            setShown(shownRef.current);
            frame = requestAnimationFrame(step);
        };
        frame = requestAnimationFrame(step);
        return () => cancelAnimationFrame(frame);
    }, [text]);

    useEffect(() => {
        const body = bodyRef.current;
        if (body && stickToBottomRef.current) body.scrollTop = body.scrollHeight;
    }, [shown]);

    const handleScroll = () => {
        const body = bodyRef.current;
        if (body) stickToBottomRef.current = body.scrollHeight - body.scrollTop - body.clientHeight < 40;
    };

    // Using static EXTRACTION_MODAL_JODIT_CONFIG

    const handleStartEditing = () => {
        const raw = (hasUserEdited ? editedText : visibleText) || source.text || '';
        const formatted = formatContentToHtml(raw);
        setEditorInitialValue(formatted);
        setEditedText(formatted);
        setIsEditing(true);
    };

    const handleDoneEditing = () => {
        let latest = null;
        if (editorRef.current) {
            if (typeof editorRef.current.getEditorValue === 'function') {
                latest = editorRef.current.getEditorValue();
            } else if (typeof editorRef.current.value === 'string') {
                latest = editorRef.current.value;
            }
        }
        const finalVal = latest !== null ? latest : editedText;
        setEditedText(finalVal);
        setHasUserEdited(true);
        setIsEditing(false);
    };

    const fullyShown = shown >= text.length;
    const complete = Boolean(result) && !error && fullyShown;
    const typing = !error && (!result || !fullyShown);

    const sourceLabel = type.kind === 'file'
        ? source.file?.name || source.label
        : isCustomText
        ? 'Direct Input'
        : source.url;
    const hasOriginal = complete && Boolean(rawText) && rawText !== text;
    const viewingOriginal = hasOriginal && showOriginal;
    // Each page's section runs from its start offset to the next page's.
    const sections = useMemo(() => (pages || []).map((page, index) => ({
        ...page,
        text: text.slice(page.start, index + 1 < pages.length ? pages[index + 1].start : text.length),
    })), [pages, text]);
    const pickingPages = complete && sections.length > 1;
    const selectedText = pickingPages && excluded.size ? sections.filter((section) => !excluded.has(section.url)).map((section) => section.text).join('').trim() : null;
    const togglePage = (url) => setExcluded((current) => {
        const next = new Set(current);
        if (next.has(url)) next.delete(url);
        else next.add(url);
        return next;
    });
    // What the reader sees before any manual edit (original or cleaned transcript, ticked pages only);
    // editing starts from it.
    const visibleText = viewingOriginal ? rawText : selectedText !== null ? selectedText : text;

    const initialText = visibleText || source.text || '';
    const isModified = Boolean(hasUserEdited && editedText !== initialText);
    const displayContent = isModified ? editedText : initialText;
    const isHtml = (str) => typeof str === 'string' && /<[a-z][\s\S]*>/i.test(str);

    // Approximate character count based on visible/raw content
    const rawVisibleChars = isHtml(displayContent)
        ? displayContent.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').length
        : displayContent.length;
    const charCount = rawVisibleChars || (result?.characters ?? 0);

    // Re-checked after every "Done" (and page/transcript switch); unchanged text comes back from the API cache.
    useEffect(() => {
        if (complete && !isEditing) setCheckedText(displayContent);
    }, [complete, isEditing, displayContent]);

    const submit = () => {
        setConfirmingSubmit(false);
        const confirmedText = isModified
            ? editedText
            : isCustomText
            ? (editedText || source.text || text)
            : undefined;
        onConfirm(result?.extraction_id || source.extraction_id, {
            editedText: confirmedText,
            useRawText: viewingOriginal,
            excludedPages: [...excluded],
        });
    };

    // Risks found (or a check still running) are confirmed once before saving.
    const handleConfirmClick = () => {
        if (enableHallucination && (review.issues.length || review.status === 'checking')) setConfirmingSubmit(true);
        else submit();
    };

    // Highlights the risky sentence (in the reading view or the open editor) and scrolls to it.
    const locateQuote = (quote) => {
        const root = isEditing ? editorRef.current?.editor : bodyRef.current;
        if (highlightPassage(root, quote)) stickToBottomRef.current = false;
    };

    // A highlight points into the text it was found in, so it goes when that text changes or the popup closes.
    useEffect(() => clearPassageHighlight, [displayContent, isEditing]);

    const highRisks = review.issues.filter((issue) => issue.Severity === 'high').length;
    const confirmDisabled = isEditing || saving || (!complete && !isCustomText && !isModified) || (pickingPages && excluded.size === sections.length && !isModified);

    return (
        <div
            className="hc-ai-modal-backdrop d-flex align-items-center justify-content-center p-3"
            role="presentation"
            style={{ overscrollBehavior: 'contain' }}
            onWheel={(e) => {
                if (e.target === e.currentTarget) {
                    e.preventDefault();
                }
            }}
        >
            <div
                className="hc-ai-modal hc-ai-extract-modal position-relative w-100 d-flex flex-column bg-white overflow-hidden shadow-lg rounded-4"
                role="dialog"
                aria-modal="true"
                aria-label="Review extracted content"
                style={{ overscrollBehavior: 'contain' }}
            >
                {/* Header */}
                <div className="hc-ai-rv-head d-flex align-items-center justify-content-between gap-3 flex-shrink-0">
                    <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                        <span className="hc-ai-rv-icon d-inline-flex align-items-center justify-content-center flex-shrink-0 text-white" style={{ '--file-color': type.color }}>
                            <MdFactCheck size={18} />
                        </span>
                        <div style={{ minWidth: 0 }}>
                            <div className="hc-ai-rv-title fw-bold hc-text-primary">{title}</div>
                            <div className="hc-ai-rv-meta d-flex align-items-center gap-2" title={`${type.label} · ${sourceLabel}`}>
                                <span className="hc-ai-rv-type rounded-pill fw-semibold flex-shrink-0" style={{ '--file-color': type.color }}>{type.label}</span>
                                <span className="text-truncate">{sourceLabel}</span>
                                {isDocument && (
                                    ocrActive ? (
                                        <span
                                            className="badge rounded-pill d-inline-flex align-items-center gap-1 flex-shrink-0 fw-semibold"
                                            style={{
                                                backgroundColor: '#e0f2fe',
                                                color: '#0284c7',
                                                border: '1px solid #bae6fd',
                                                fontSize: '11px',
                                                padding: '2px 8px',
                                                lineHeight: '1.4',
                                            }}
                                            title="Extracted with Vision AI OCR enabled"
                                        >
                                            <MdDocumentScanner size={13} />
                                            <span>OCR Enabled</span>
                                        </span>
                                    ) : (
                                        <span
                                            className="badge rounded-pill d-inline-flex align-items-center gap-1 flex-shrink-0 fw-medium"
                                            style={{
                                                backgroundColor: '#f1f5f9',
                                                color: '#64748b',
                                                border: '1px solid #e2e8f0',
                                                fontSize: '11px',
                                                padding: '2px 8px',
                                                lineHeight: '1.4',
                                            }}
                                            title="Standard extraction without OCR"
                                        >
                                            <span>OCR Disabled</span>
                                        </span>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                        <button
                            type="button"
                            className={`hc-ai-rv-btn d-inline-flex align-items-center gap-1 fw-semibold ${isEditing ? 'is-primary' : ''}`}
                            onClick={isEditing ? handleDoneEditing : handleStartEditing}
                            disabled={!text && !editedText && !source.text}
                            title={isEditing ? 'Done editing' : 'Edit extracted content'}
                        >
                            {isEditing ? <MdCheck size={16} /> : <MdEdit size={16} />}
                            <span>{isEditing ? 'Done' : 'Edit'}</span>
                        </button>
                        <button
                            type="button"
                            className="hc-ai-rv-close border-0 rounded-circle d-inline-flex align-items-center justify-content-center"
                            onClick={onCancel}
                            disabled={saving}
                            aria-label="Close"
                            title="Close"
                        >
                            <MdClose size={18} />
                        </button>
                    </div>
                </div>

                <div className="hc-ai-extract-columns d-flex flex-grow-1" style={{ minHeight: 0 }}>
                    {enableHallucination && <AiHallucinationPanel review={review} onLocate={locateQuote} />}

                    <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0, minHeight: 0 }}>
                        {/* Status toolbar */}
                        <div className="hc-ai-rv-toolbar d-flex align-items-center justify-content-between gap-2 flex-shrink-0" aria-live="polite">
                            {error ? (
                                <>
                                    <div className="hc-ai-src-error d-flex align-items-center gap-1" role="alert">
                                        <MdErrorOutline size={16} className="flex-shrink-0" />
                                        <span>{error}</span>
                                    </div>
                                    <button type="button" className="hc-ai-rv-btn d-inline-flex align-items-center gap-1 fw-semibold" onClick={onRetry}>
                                        <MdRefresh size={16} />
                                        Try again
                                    </button>
                                </>
                            ) : isEditing ? (
                                <div className="d-flex align-items-center gap-2 hc-ai-rv-status is-editing">
                                    <MdEdit size={15} className="flex-shrink-0" />
                                    <span>Editing · click Done to apply your changes and re-check</span>
                                </div>
                            ) : complete ? (
                                <>
                                    <div className="d-flex align-items-center gap-2 hc-ai-rv-status" style={{ minWidth: 0 }}>
                                        <MdCheckCircle size={16} className="hc-ai-src-ok flex-shrink-0" />
                                        <span className="text-truncate">
                                            {charCount.toLocaleString()} characters
                                            {isModified ? (source.sourceType === 'document' ? ' · edited, will be saved as a PDF' : ' · edited') : ''}
                                            {!isModified && pickingPages ? ` · ${sections.length - excluded.size} of ${sections.length} pages` : ''}
                                        </span>
                                    </div>
                                    {hasOriginal && !isModified ? (
                                        <div className="hc-ai-rv-seg d-inline-flex rounded-pill flex-shrink-0" role="group" aria-label="Transcript version">
                                            <button type="button" className={`rounded-pill ${viewingOriginal ? '' : 'is-active'}`} onClick={() => setShowOriginal(false)} disabled={saving}>Cleaned</button>
                                            <button type="button" className={`rounded-pill ${viewingOriginal ? 'is-active' : ''}`} onClick={() => setShowOriginal(true)} disabled={saving}>Original</button>
                                        </div>
                                    ) : null}
                                </>
                            ) : (
                                <div className="d-flex align-items-center gap-2 hc-ai-rv-status">
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                    <span>{cleaning || pages?.length ? status : text ? 'Extracting...' : status || 'Extracting...'}</span>
                                    {shown ? <span className="text-muted">· {shown.toLocaleString()} characters</span> : null}
                                </div>
                            )}
                        </div>

                        {pickingPages ? (
                            <div className="px-4 pt-3 flex-shrink-0">
                                <div className="hc-ai-extract-pages rounded-3 overflow-auto" role="group" aria-label="Pages to save">
                                    {sections.map((section) => (
                                        <label key={section.url} className="hc-ai-extract-page d-flex align-items-center gap-2 px-3 py-2 mb-0">
                                            <input
                                                type="checkbox"
                                                className="form-check-input flex-shrink-0 mt-0"
                                                checked={!excluded.has(section.url)}
                                                onChange={() => togglePage(section.url)}
                                                disabled={saving || isEditing || isModified}
                                            />
                                            <span className="flex-grow-1 text-truncate" style={{ minWidth: 0 }} title={section.url}>
                                                <span className="fw-semibold">{section.title}</span>
                                                <span className="hc-ai-file-meta ms-2">{section.url}</span>
                                            </span>
                                            <span className="hc-ai-file-meta flex-shrink-0">{section.text.length.toLocaleString()} chars</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        {/* Content */}
                        {isEditing ? (
                            <div className={`hc-ai-jodit-wrap flex-grow-1 m-3 ${isDocument ? 'is-document' : ''}`} style={{ minHeight: 0 }}>
                                <JoditEditor
                                    ref={editorRef}
                                    value={editorInitialValue}
                                    config={EXTRACTION_MODAL_JODIT_CONFIG}
                                    onBlur={(newContent) => {
                                        setHasUserEdited(true);
                                        setEditedText(newContent);
                                    }}
                                    onChange={() => {}}
                                />
                            </div>
                        ) : (
                            <div
                                ref={bodyRef}
                                onScroll={handleScroll}
                                className={`hc-ai-extract-text hc-ai-rv-text flex-grow-1 overflow-auto ${isDocument ? 'is-document' : ''} ${isHtml(displayContent) ? 'hc-ai-extract-html-mode' : ''}`}
                                style={{ overscrollBehavior: 'contain' }}
                                tabIndex={0}
                                aria-label="Extracted content"
                            >
                                {isHtml(displayContent) ? (
                                    <div
                                        className={`hc-ai-rendered-content ${isDocument ? 'hc-ai-doc-page' : ''}`}
                                        dangerouslySetInnerHTML={{ __html: formatContentToHtml(displayContent) }}
                                    />
                                ) : (
                                    <>
                                        {shown || isModified || editedText ? (
                                            isModified ? editedText : !typing ? (visibleText || editedText) : text.slice(0, shown)
                                        ) : null}
                                        {typing && !isModified ? <span className="hc-ai-extract-caret" aria-hidden="true" /> : null}
                                        {!shown && !typing && !editedText && !text ? (
                                            <span className="text-muted">No content was extracted.</span>
                                        ) : null}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="hc-ai-rv-foot d-flex flex-wrap justify-content-between align-items-center gap-2 flex-shrink-0">
                    <div className="hc-ai-rv-status small" style={{ minWidth: 0 }}>
                        {!enableHallucination ? null : review.status === 'checking' ? (
                            <span className="text-muted">Checking for hallucination risks...</span>
                        ) : review.status === 'done' ? (
                            review.issues.length ? (
                                <span className="d-inline-flex align-items-center gap-1">
                                    <MdReportGmailerrorred size={16} className="hc-ai-halluc-confirm-icon flex-shrink-0" />
                                    {review.issues.length} passage{review.issues.length === 1 ? '' : 's'} to review{highRisks ? `, ${highRisks} high risk` : ''}
                                </span>
                            ) : (
                                <span className="d-inline-flex align-items-center gap-1">
                                    <MdCheckCircle size={16} className="hc-ai-src-ok flex-shrink-0" />
                                    No hallucination risks found
                                </span>
                            )
                        ) : null}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <button
                            type="button"
                            className="hc-ai-btn overflow-hidden d-inline-flex align-items-center justify-content-center gap-2 fw-bold text-nowrap hc-ai-btn-outline"
                            onClick={onCancel}
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="hc-ai-btn overflow-hidden d-inline-flex align-items-center justify-content-center gap-2 fw-bold text-nowrap hc-ai-btn-primary px-4"
                            onClick={handleConfirmClick}
                            disabled={confirmDisabled}
                            title={
                                isEditing
                                    ? "Click 'Done' before confirming"
                                    : !complete && !isCustomText && !isModified
                                    ? 'Available once the full content has been extracted'
                                    : pickingPages && excluded.size === sections.length && !isModified
                                    ? 'Select at least one page'
                                    : undefined
                            }
                        >
                            {saving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                    {savingLabel || 'Saving...'}
                                </>
                            ) : (
                                <>
                                    <MdCheckCircle size={18} />
                                    Confirm &amp; Save
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {confirmingSubmit ? (
                    <div className="hc-ai-halluc-confirm-backdrop position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3" role="presentation">
                        <div className="hc-ai-halluc-confirm bg-white rounded-4 shadow-lg p-4" role="alertdialog" aria-modal="true" aria-labelledby="hc-ai-halluc-confirm-title">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <MdReportGmailerrorred size={22} className="hc-ai-halluc-confirm-icon flex-shrink-0" />
                                <h6 className="fw-bold mb-0" id="hc-ai-halluc-confirm-title">
                                    {review.status === 'checking' ? 'Hallucination check still running' : `${review.issues.length} hallucination risk${review.issues.length === 1 ? '' : 's'} found`}
                                </h6>
                            </div>
                            <p className="small mb-3">
                                {review.status === 'checking'
                                    ? 'The text has not finished being checked. Submit it anyway, or go back and wait for the results?'
                                    : 'The passages listed on the left could make the assistant give wrong answers. Skip them and submit this text, or go back and edit it?'}
                            </p>
                            <div className="d-flex justify-content-end gap-2">
                                <button type="button" className="hc-ai-btn d-inline-flex align-items-center gap-1 fw-bold text-nowrap hc-ai-btn-outline" onClick={() => {
                                        setConfirmingSubmit(false);
                                        if (review.status !== 'checking') handleStartEditing();
                                    }} autoFocus>
                                    {review.status === 'checking' ? 'Go back' : 'Go back & edit'}
                                </button>
                                <button type="button" className="hc-ai-btn d-inline-flex align-items-center gap-1 fw-bold text-nowrap hc-ai-btn-primary" onClick={submit}>
                                    {review.status === 'checking' ? 'Submit anyway' : 'Skip & submit'}
                                </button>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
